/* =========================================================================
   AdminTwoFactorDialog.tsx – set up / remove the admin's own TOTP key

   CCPayment's withdrawal API authenticates with the app secret alone, so the
   second factor on a payout is ours: an admin must prove they hold the phone
   before coins move. SuperAdmins only — they're the only ones who can approve.
   ========================================================================= */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Alert, TextField, Typography, Stack,
} from '@mui/material';
import { apiFetch } from '../../utils/api';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Fired after enable/disable so the parent can refresh its own 2FA state. */
  onChanged?: () => void;
}

interface StatusRes { isEnabled: boolean; hasInitiated: boolean; isSuperAdmin: boolean }

const cardBg = { bgcolor: '#0E1831' };

const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#10182E',
    '& fieldset': { borderColor: '#1E2D55' },
    '&:hover fieldset': { borderColor: '#2B6EF5' },
    '&.Mui-focused fieldset': { borderColor: '#2B6EF5' },
  },
  '& input': { color: '#F9F9F9' },
  '& .MuiInputLabel-root': { color: '#8384A5' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#2B6EF5' },
};

export default function AdminTwoFactorDialog({ open, onClose, onChanged }: Props) {
  const [status, setStatus] = useState<StatusRes | null>(null);
  const [qr, setQr] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  const loadStatus = useCallback(async () => {
    setErr('');
    try {
      setStatus(await apiFetch<StatusRes>('/api/ccpayment/2fa/status'));
    } catch (e: any) {
      setErr(e?.message || 'Could not load 2FA status');
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setQr(''); setSecret(''); setCode(''); setErr(''); setOk('');
    loadStatus();
  }, [open, loadStatus]);

  const startSetup = async () => {
    setBusy(true); setErr(''); setOk('');
    try {
      const res = await apiFetch<{ qrCode: string; secret: string }>('/api/ccpayment/2fa/enable', { method: 'POST' });
      setQr(res.qrCode);
      setSecret(res.secret);
    } catch (e: any) {
      setErr(e?.message || 'Could not start 2FA setup');
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async () => {
    setBusy(true); setErr(''); setOk('');
    try {
      await apiFetch('/api/ccpayment/2fa/setup-verify', {
        method: 'POST',
        body: JSON.stringify({ token: code.trim() }),
      });
      setOk('2FA is now enabled. You will need a code to approve or reject withdrawals.');
      setQr(''); setSecret(''); setCode('');
      await loadStatus();
      onChanged?.();
    } catch (e: any) {
      setErr(e?.message || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true); setErr(''); setOk('');
    try {
      await apiFetch('/api/ccpayment/2fa/disable', {
        method: 'POST',
        body: JSON.stringify({ token: code.trim() }),
      });
      setOk('2FA disabled.');
      setCode('');
      await loadStatus();
      onChanged?.();
    } catch (e: any) {
      setErr(e?.message || 'Could not disable 2FA');
    } finally {
      setBusy(false);
    }
  };

  const enabled = !!status?.isEnabled;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ ...cardBg, color: '#F9F9F9', fontSize: '1rem' }}>
        Withdrawal 2FA
      </DialogTitle>

      <DialogContent sx={{ ...cardBg, pt: 2 }}>
        {status && !status.isSuperAdmin && (
          <Alert severity="info" sx={{ bgcolor: 'rgba(85,129,247,0.1)', color: '#5581F7' }}>
            Only a SuperAdmin can hold a withdrawal 2FA key.
          </Alert>
        )}

        {status?.isSuperAdmin && (
          <>
            <Typography sx={{ color: '#8384A5', fontSize: '0.75rem', mb: 2 }}>
              Status:{' '}
              <span style={{ color: enabled ? '#0ECC68' : '#FFC23F', fontWeight: 700 }}>
                {enabled ? 'Enabled' : 'Not enabled'}
              </span>
            </Typography>

            {/* --- Not enabled yet: show/scan the QR --- */}
            {!enabled && !qr && (
              <Button
                onClick={startSetup}
                disabled={busy}
                variant="contained"
                fullWidth
                sx={{ bgcolor: '#2B6EF5', '&:hover': { bgcolor: '#4A85F7' } }}
              >
                {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Set up 2FA'}
              </Button>
            )}

            {!enabled && qr && (
              <Stack spacing={1.5} alignItems="center">
                <Typography sx={{ color: '#8384A5', fontSize: '0.75rem', textAlign: 'center' }}>
                  Scan this in Google Authenticator, then enter the 6-digit code it shows.
                </Typography>
                <Box component="img" src={qr} alt="2FA QR code" sx={{ width: 190, height: 190, borderRadius: 1, bgcolor: '#fff', p: 1 }} />
                <Typography sx={{ color: '#8384A5', fontSize: '0.65rem', wordBreak: 'break-all', textAlign: 'center' }}>
                  Can't scan? Enter this key manually:<br />
                  <span style={{ color: '#F9F9F9' }}>{secret}</span>
                </Typography>
                <TextField
                  autoFocus fullWidth size="small" label="6-digit code" placeholder="000000"
                  value={code} onChange={(e) => setCode(e.target.value)}
                  inputProps={{ maxLength: 6 }} sx={inputSx}
                />
                <Button
                  onClick={confirmSetup} disabled={busy} variant="contained" fullWidth
                  sx={{ bgcolor: '#0ECC68', '&:hover': { bgcolor: '#0BB35C' } }}
                >
                  {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Verify & enable'}
                </Button>
              </Stack>
            )}

            {/* --- Already enabled: allow removal, but only with a live code --- */}
            {enabled && (
              <Stack spacing={1.5}>
                <Typography sx={{ color: '#8384A5', fontSize: '0.75rem' }}>
                  To turn 2FA off, confirm with a current code from your authenticator app.
                </Typography>
                <TextField
                  fullWidth size="small" label="6-digit code" placeholder="000000"
                  value={code} onChange={(e) => setCode(e.target.value)}
                  inputProps={{ maxLength: 6 }} sx={inputSx}
                />
                <Button
                  onClick={disable} disabled={busy} variant="outlined"
                  sx={{ color: '#E01B4F', borderColor: '#E01B4F', '&:hover': { borderColor: '#E01B4F', bgcolor: 'rgba(224,27,79,0.08)' } }}
                >
                  {busy ? <CircularProgress size={18} sx={{ color: '#E01B4F' }} /> : 'Disable 2FA'}
                </Button>
              </Stack>
            )}
          </>
        )}

        {err && <Alert severity="error" sx={{ mt: 2, bgcolor: 'rgba(224,27,79,0.1)', color: '#E01B4F', fontSize: '0.75rem' }}>{err}</Alert>}
        {ok && <Alert severity="success" sx={{ mt: 2, bgcolor: 'rgba(14,204,104,0.1)', color: '#0ECC68', fontSize: '0.75rem' }}>{ok}</Alert>}
      </DialogContent>

      <DialogActions sx={{ ...cardBg, px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ color: '#8384A5' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
